import { useState } from 'react';
import { T, useGT } from 'gt-react';
import type { SalesOrderV3Detail } from '@erp/rpc';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Input } from 'src/components/ui/input';
import { cn } from '@/lib/utils';
import {
  calculateTotal,
  formatCurrency,
  getCurrencySymbol,
} from 'src/pages/authenticated/sales-orders/components/line-item-table/priceUtils';
import {
  useUpdateDraftSalesOrderLineItem,
  useDeleteDraftSalesOrderLineItem,
  useValidateDraftSalesOrderInventory,
} from '@/api/rpc/hooks/accounts-receivable/useDraftSalesOrders';
import {
  TrashIcon,
  CheckCircleIcon,
  WarningIcon,
  QuestionIcon,
  ProhibitIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import {
  useDraftDiscountCandidatesV2,
  useDraftLineStacksV2,
  useValidateDraftDiscountsV2,
} from '@/api/rpc/hooks/accounts-receivable/useDraftDiscountsV2';
import { PriceDeltaLine } from './DeltaPriceCell';
import { LineStatusChip, type LineStatus } from './LineStatusChip';
import { LineDiscountsDialog } from './LineDiscountsDialog';
import { LineMatchPopover } from './LineMatchPopover';
import { useHoverSync } from 'src/components/document-provenance/HoverSyncContext';
import { TooltipInfoIcon } from 'src/components/common/TooltipInfoIcon';
import { InventoryStatus, EDITABLE_LINE_DATA_PATHS } from '@erp/shared-types';
import type { EditableLineDataPathSpec } from '@erp/shared-types';
import type { DraftInventoryCheckResult } from '@erp/rpc';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

type LineItem = SalesOrderV3Detail['lineItems'][number];

/**
 * Discounts v2 per-line feed merged into each line item before rendering, so
 * org-configured custom columns can reference it by dot-path — e.g.
 * `discount.effectiveUnitPrice` (currency) for the price after discounts.
 */
type LineDiscountFeed = {
  /** Stack result: unit price after the applied discounts; null when no stack. */
  finalUnitPrice: number | null;
  /** finalUnitPrice when a stack is applied, else the line's PO unit price. */
  effectiveUnitPrice: number | null;
  /** (list − final) × qty for the applied stack; null when no stack. */
  totalDiscountAmount: number | null;
  /** True while the stack's group is still awaiting approval. */
  pending: boolean;
};
type EnrichedLineItem = LineItem & { discount: LineDiscountFeed };

/** Optional second line under a column's main value (prefix + field/compute). */
type ColumnSubline = {
  prefix?: string;
  dataPath?: string;
  compute?: { left: string; op: string; right: string };
  format?: string;
  decimals?: number;
};

type Column = {
  key: string;
  label: string;
  width: string | null;
  alignRight: boolean;
  /** Effective alignment (org override → catalog default → left). */
  align?: 'left' | 'center' | 'right';
  /** Custom (org-added) columns: dot-path into the line item + display format.
   *  The Discounts v2 feed is available under `discount.*` (see LineDiscountFeed). */
  dataPath?: string;
  format?: string;
  /** Decimal places for numeric/percent formatting (custom columns). */
  decimals?: number;
  /** Two-field arithmetic for a computed custom column (instead of dataPath). */
  compute?: { left: string; op: string; right: string };
  /** Help text shown as a tooltip on the column header. */
  tooltip?: string;
  /** Optional second line under the main value (works on any column). */
  subline?: ColumnSubline;
  /** Inline-editable cells (manual override); writable data paths only. */
  editable?: boolean;
};

/**
 * A column's effective alignment. `align` is resolver-provided; the fallbacks
 * cover cached payloads from before it existed (alignRight + the centered
 * match column).
 */
function columnAlign(col: Column): 'left' | 'center' | 'right' {
  if (col.align) return col.align;
  if (col.alignRight) return 'right';
  return col.key === 'match' ? 'center' : 'left';
}

function toNum(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : null;
}

/** Narrow an unknown (e.g. a resolved dot-path value) to a numeric-coercible type. */
function asNumeric(value: unknown): string | number | null {
  return typeof value === 'number' || typeof value === 'string' ? value : null;
}

/** Resolve a dot-notation path against the line item (for custom columns). */
function getByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc != null && typeof acc === 'object' && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/** Format a number per a column's display `format` + optional decimal places. */
function formatNumber(
  n: number,
  format: string | undefined,
  decimals: number | undefined,
  currency?: string | null,
): string {
  switch (format) {
    case 'currency':
      // Honor an explicit decimals override; otherwise the app's default
      // (accounting-style) currency formatting.
      return decimals != null
        ? new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          }).format(n)
        : formatCurrency(n, currency);
    case 'percent':
      return `${n.toFixed(decimals ?? 1)}%`;
    // `integer` / `float` retained for back-compat with previously-saved configs;
    // the editor now offers a single merged "Number" format.
    case 'integer':
      return n.toFixed(decimals ?? 0);
    case 'float':
    case 'number':
      return decimals != null ? n.toFixed(decimals) : String(n);
    default:
      return decimals != null ? n.toFixed(decimals) : String(n);
  }
}

/** Apply a computed column's operator to its two operands. */
function applyCompute(left: number, op: string, right: number): number | null {
  switch (op) {
    case 'subtract':
      return left - right;
    case 'add':
      return left + right;
    case 'multiply':
      return left * right;
    case 'divide':
      return right === 0 ? null : left / right;
    // The discount a `left` selling price represents against a `right` list price.
    case 'percent_off':
      return right === 0 ? null : ((right - left) / right) * 100;
    default:
      return null;
  }
}

/** Render a custom column's resolved value, applying its display format. */
function formatCustomValue(
  value: unknown,
  format: string | undefined,
  currency?: string | null,
  decimals?: number,
): React.ReactNode {
  if (value == null || value === '') return '—';
  if (
    (format === 'currency' ||
      format === 'percent' ||
      format === 'integer' ||
      format === 'float' ||
      format === 'number') &&
    (typeof value === 'number' || typeof value === 'string')
  ) {
    const n = toNum(value);
    return n != null
      ? formatNumber(n, format, decimals, currency)
      : String(value);
  }
  if (format === 'date') {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Read a string-valued key off the untyped `additionalData` bag. */
function additionalDataString(
  additionalData: LineItem['additionalData'],
  key: string,
): string | null {
  const value = additionalData?.[key];
  return typeof value === 'string' ? value : null;
}

/** Case-insensitive "includes" over a line's identifying text + line number. */
function lineMatchesFilter(li: LineItem, needle: string): boolean {
  const haystack = [
    li.customerIdentifier,
    li.customerDescription,
    li.productSku,
    additionalDataString(li.additionalData, 'sku'),
    additionalDataString(li.additionalData, 'upc'),
    String(li.lineNumber + 1),
  ];
  return haystack.some(
    (value) => value != null && value.toLowerCase().includes(needle),
  );
}

/**
 * Derive a line's status chip from the stamped facts available today. The full
 * needs/await/queued/approved/special lifecycle is wired to discount + approval
 * state in later phases; for now a below-list line surfaces "needs discount
 * approval", everything else is left unmarked.
 */
function deriveStatus(li: LineItem): LineStatus | null {
  if (li.listPriceMismatch) return 'needs';
  return null;
}

/** The line fields an inline edit can write (matches the update contract). */
type LineEditPatch = {
  quantity?: number | null;
  unitPrice?: number | null;
  amount?: number | null;
  unit?: string | null;
  warehouse?: string | null;
  customerIdentifier?: string | null;
  customerDescription?: string | null;
};

/** Per-field patch builders — numeric fields take num, text fields take str. */
const LINE_EDIT_PATCH_BUILDERS: Record<
  EditableLineDataPathSpec['patchKey'],
  (num: number | null, str: string | null) => LineEditPatch
> = {
  quantity: (num) => ({ quantity: num }),
  unitPrice: (num) => ({ unitPrice: num }),
  amount: (num) => ({ amount: num }),
  unit: (_num, str) => ({ unit: str }),
  warehouse: (_num, str) => ({ warehouse: str }),
  customerIdentifier: (_num, str) => ({ customerIdentifier: str }),
  customerDescription: (_num, str) => ({ customerDescription: str }),
};

/** Map an editable column's committed value onto its writable patch field. */
function buildEditPatch(
  spec: EditableLineDataPathSpec,
  next: number | string | null,
): LineEditPatch {
  return LINE_EDIT_PATCH_BUILDERS[spec.patchKey](
    typeof next === 'number' ? next : null,
    typeof next === 'string' && next.trim() !== '' ? next : null,
  );
}

/** What an editable cell edits and where a commit goes (typed per widget). */
type InlineEdit =
  | {
      kind: 'number';
      value: number | null;
      onCommit: (n: number | null) => void;
    }
  | {
      kind: 'text';
      value: string | null;
      onCommit: (s: string | null) => void;
    };

/** Parse the input's raw text per widget kind and commit only real changes. */
function commitInlineEdit(edit: InlineEdit, raw: string): void {
  if (edit.kind === 'number') {
    const next = raw === '' ? null : Number(raw);
    if (next !== edit.value && (next === null || Number.isFinite(next))) {
      edit.onCommit(next);
    }
    return;
  }
  if (raw !== (edit.value ?? '')) edit.onCommit(raw === '' ? null : raw);
}

/** The width an editable cell keeps in both states so the column never shifts. */
function inlineEditWidth(edit: InlineEdit): string {
  return edit.kind === 'number' ? 'w-24' : 'w-32';
}

/** Plain 2-decimal number (no currency symbol) for an idle currency cell —
 *  the symbol is rendered separately as the field's prefix. */
function formatPlainAmount(n: number): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** The muted currency symbol pinned inside the left edge of an editable field. */
function InlinePrefix({ prefix }: { prefix: string }) {
  return (
    <span className="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-sm text-muted-foreground">
      {prefix}
    </span>
  );
}

/**
 * The active input of an editable cell. A `prefix` (currency symbol) sits
 * inside the field while typing. Commits on blur/Enter; Escape reverts.
 */
function InlineEditInput({
  edit,
  align,
  prefix,
  onDone,
}: {
  edit: InlineEdit;
  align: 'left' | 'right';
  prefix?: string;
  onDone: () => void;
}) {
  const isNumber = edit.kind === 'number';
  return (
    <div className={cn('relative inline-block', inlineEditWidth(edit))}>
      {prefix ? <InlinePrefix prefix={prefix} /> : null}
      <Input
        type={isNumber ? 'number' : undefined}
        step={isNumber ? 'any' : undefined}
        autoFocus
        defaultValue={edit.value ?? ''}
        className={cn(
          'h-7 px-2',
          isNumber && 'tabular-nums',
          align === 'right' && 'text-right',
          prefix && 'pl-5',
        )}
        onBlur={(e) => {
          onDone();
          commitInlineEdit(edit, e.target.value.trim());
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            e.currentTarget.value =
              edit.value == null ? '' : String(edit.value);
            onDone();
          }
        }}
      />
    </div>
  );
}

/**
 * Click-to-edit cell (number or text). Idle, it is framed like an input so the
 * cell reads as editable, and a currency `prefix` sits inside the frame in
 * both states — so the idle `display` must NOT carry the symbol itself.
 */
function InlineEditCell({
  edit,
  display,
  editable,
  align = 'left',
  prefix,
}: {
  edit: InlineEdit;
  display: React.ReactNode;
  editable: boolean;
  align?: 'left' | 'right';
  prefix?: string;
}) {
  const t = useGT();
  const [editing, setEditing] = useState(false);

  if (!editable) return <>{display}</>;

  if (editing) {
    return (
      <InlineEditInput
        edit={edit}
        align={align}
        prefix={prefix}
        onDone={() => setEditing(false)}
      />
    );
  }

  return (
    <div className={cn('relative inline-block', inlineEditWidth(edit))}>
      {prefix ? <InlinePrefix prefix={prefix} /> : null}
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={cn(
          'flex h-7 w-full cursor-pointer items-center rounded-md border border-input bg-background px-2 text-sm shadow-xs hover:bg-accent',
          align === 'right' ? 'justify-end tabular-nums' : 'justify-start',
          prefix && 'pl-5',
        )}
        title={t('Click to edit')}
      >
        <span className="truncate">{display}</span>
      </button>
    </div>
  );
}

const INVENTORY_STATUS_VISUALS: Record<
  string,
  {
    Icon: typeof CheckCircleIcon;
    iconClass: string;
    label: string;
    chipClass: string;
  }
> = {
  [InventoryStatus.SUFFICIENT]: {
    Icon: CheckCircleIcon,
    iconClass: 'text-green-600',
    label: 'In Stock',
    chipClass: 'bg-green-600/10 text-green-700',
  },
  [InventoryStatus.INSUFFICIENT]: {
    Icon: WarningIcon,
    iconClass: 'text-orange-600',
    label: 'Low Stock',
    chipClass: 'bg-orange-600/10 text-orange-700',
  },
  [InventoryStatus.INACTIVE]: {
    Icon: ProhibitIcon,
    iconClass: 'text-red-600',
    label: 'Inactive',
    chipClass: 'bg-red-600/10 text-red-700',
  },
  [InventoryStatus.UNKNOWN]: {
    Icon: QuestionIcon,
    iconClass: 'text-muted-foreground',
    label: 'Unknown',
    chipClass: 'bg-foreground/8 text-muted-foreground',
  },
};

function formatQuantity(value: number | null | undefined): string {
  if (value == null) return '-';
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** The v2 InventoryBadge chip, rebuilt in tailwind (v3 avoids MUI). */
function InventoryChip({
  Icon,
  iconClass,
  label,
  chipClass,
  spin = false,
}: {
  Icon: typeof CheckCircleIcon;
  iconClass: string;
  label: string;
  chipClass: string;
  spin?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex min-w-[110px] cursor-help items-center justify-center gap-1 rounded px-2 py-1 text-xs font-medium whitespace-nowrap',
        chipClass,
      )}
    >
      <Icon size={14} className={cn(iconClass, spin && 'animate-spin')} />
      {label}
    </span>
  );
}

/** Label/value row inside the inventory tooltip (v2 layout). */
function InventoryTooltipRow({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex justify-between gap-4">
      <span>{label}</span>
      <span className={cn('font-medium', valueClass ?? 'text-white')}>
        {value}
      </span>
    </div>
  );
}

/** The v2 InventoryBadge tooltip body, keyed by the check result. */
function InventoryTooltipBody({
  result,
  t,
}: {
  result: DraftInventoryCheckResult;
  t: ReturnType<typeof useGT>;
}) {
  const {
    status,
    requestedQuantity,
    availableQuantity,
    onhandQuantity,
    reservedQuantity,
    itemNumber,
    organizationCode,
    errorMessage,
  } = result;

  if (status === InventoryStatus.UNKNOWN && errorMessage) {
    return (
      <div className="space-y-1 text-xs">
        <div className="font-medium">{t('Unable to check inventory')}</div>
        <div className="text-gray-300">{errorMessage}</div>
      </div>
    );
  }

  const itemOrgFooter =
    itemNumber || organizationCode ? (
      <div className="border-t border-gray-600 pt-1 text-gray-400">
        {itemNumber ? (
          <div>
            {t('Item')}: {itemNumber}
          </div>
        ) : null}
        {organizationCode ? (
          <div>
            {t('Org')}: {organizationCode}
          </div>
        ) : null}
      </div>
    ) : null;

  if (status === InventoryStatus.INACTIVE) {
    return (
      <div className="space-y-1 text-xs">
        <div className="font-medium">{t('Item Inactive')}</div>
        <div className="text-gray-300">
          {t('This item is inactive and will be excluded from the order.')}
        </div>
        {itemOrgFooter}
      </div>
    );
  }

  const shortage =
    availableQuantity !== null ? requestedQuantity - availableQuantity : null;
  const title =
    status === InventoryStatus.SUFFICIENT
      ? t('Inventory Available')
      : status === InventoryStatus.INSUFFICIENT
        ? t('Insufficient Inventory')
        : t('Inventory Status Unknown');

  return (
    <div className="space-y-1 text-xs">
      <div className="font-medium">{title}</div>
      <div className="space-y-0.5 text-gray-300">
        <InventoryTooltipRow
          label={t('Requested:')}
          value={formatQuantity(requestedQuantity)}
        />
        <InventoryTooltipRow
          label={t('Available:')}
          value={formatQuantity(availableQuantity)}
          valueClass={
            status === InventoryStatus.INSUFFICIENT
              ? 'text-orange-400'
              : undefined
          }
        />
        {status === InventoryStatus.INSUFFICIENT && shortage !== null ? (
          <div className="flex justify-between gap-4 text-orange-400">
            <span>{t('Shortage:')}</span>
            <span className="font-medium">{formatQuantity(shortage)}</span>
          </div>
        ) : null}
      </div>
      {onhandQuantity !== null || reservedQuantity !== null ? (
        <div className="space-y-0.5 border-t border-gray-600 pt-1 text-gray-400">
          {onhandQuantity !== null ? (
            <InventoryTooltipRow
              label={t('On-hand:')}
              value={formatQuantity(onhandQuantity)}
              valueClass="text-gray-400"
            />
          ) : null}
          {reservedQuantity !== null ? (
            <InventoryTooltipRow
              label={t('Reserved:')}
              value={formatQuantity(reservedQuantity)}
              valueClass="text-gray-400"
            />
          ) : null}
        </div>
      ) : null}
      {itemOrgFooter}
    </div>
  );
}

/**
 * Inventory column cell — the v2 InventoryBadge design (icon chip + detailed
 * quantity tooltip) applied to v3. Prefers the live availability-check result,
 * falls back to the status persisted on the line (written the last time any
 * check ran), and shows a neutral "-" badge when neither exists.
 */
/**
 * A line the ERP order was submitted WITHOUT (unmatched product, inactive in
 * the ERP, pulled after an ERP rejection) — the "exception item" the
 * submission email lists. Replaces the inventory chip, with the reason in the
 * tooltip.
 */
function ExcludedLineChip({ reason }: { reason: string | undefined }) {
  const t = useGT();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <InventoryChip
            Icon={ProhibitIcon}
            iconClass="text-red-600"
            chipClass="bg-red-600/10 text-red-700"
            label={t('Excluded')}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1 text-xs">
          <div className="font-medium">{t('Excluded from the ERP order')}</div>
          <div className="text-gray-300">
            {reason ?? t('This line was left out of the submitted order.')}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function InventoryStatusCell({
  result,
  fallbackStatus,
  loading,
}: {
  result?: DraftInventoryCheckResult;
  fallbackStatus?: string | null;
  loading: boolean;
}) {
  const t = useGT();

  if (!result && loading) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <InventoryChip
              Icon={CircleNotchIcon}
              iconClass="text-blue-500"
              label={t('Checking...')}
              chipClass="bg-blue-600/10 text-blue-700"
              spin
            />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">
            {t('Checking inventory availability...')}
          </span>
        </TooltipContent>
      </Tooltip>
    );
  }

  const status = result?.status ?? fallbackStatus;
  if (!status) {
    const neutral = INVENTORY_STATUS_VISUALS[InventoryStatus.UNKNOWN];
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <InventoryChip {...neutral} label="-" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">
            {t('No inventory data for this line')}
          </span>
        </TooltipContent>
      </Tooltip>
    );
  }

  const visual =
    INVENTORY_STATUS_VISUALS[status] ??
    INVENTORY_STATUS_VISUALS[InventoryStatus.UNKNOWN];
  // Literal t() calls per status (i18n extraction needs static strings).
  const label =
    status === InventoryStatus.SUFFICIENT
      ? t('In Stock')
      : status === InventoryStatus.INSUFFICIENT
        ? t('Low Stock')
        : status === InventoryStatus.INACTIVE
          ? t('Inactive')
          : status === InventoryStatus.UNKNOWN
            ? t('Unknown')
            : status;
  const chip = (
    <span>
      <InventoryChip {...visual} label={label} />
    </span>
  );
  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent>
        {result ? (
          <InventoryTooltipBody result={result} t={t} />
        ) : (
          <span className="text-xs">
            {t('Status from the last inventory check on this draft.')}
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export function DeltaPricedLineTable({
  draftId,
  lineItems,
  columns,
  currency,
  customerId,
  orderDate,
  editable = false,
  routingEditable = false,
  canCreateDiscount = false,
  filterText = '',
  discountsDialogLineId,
  onDiscountsDialogLineChange,
}: {
  draftId: string;
  lineItems: LineItem[];
  columns: Column[];
  currency?: string | null;
  customerId?: string | null;
  /** Draft order date — seeds the per-line create modal's effective start. */
  orderDate?: string;
  editable?: boolean;
  /** Only the WH (warehouse) column stays editable while the rest is locked. */
  routingEditable?: boolean;
  /** Whether the user may create discounts (per-line create modal). */
  canCreateDiscount?: boolean;
  /** Free-text filter over the line's codes/description/number (display only). */
  filterText?: string;
  /**
   * Controlled "which line's discounts dialog is open" (null = none). When
   * provided, the review page owns the state so the AI assistant can open it.
   */
  discountsDialogLineId?: string | null;
  onDiscountsDialogLineChange?: (lineId: string | null) => void;
}) {
  const t = useGT();
  const { hoveredLineId, setHoveredLineId } = useHoverSync();
  const updateLineItem = useUpdateDraftSalesOrderLineItem(draftId);
  const deleteLineItem = useDeleteDraftSalesOrderLineItem(draftId);
  // Inventory availability: run the same check v2 uses whenever the table
  // shows the built-in inventory column. The endpoint also persists
  // inventoryStatus per line, so resolvers/submission see it too.
  const hasInventoryColumn = columns.some(
    (c) => c.key === 'inventory' && !c.dataPath && !c.compute,
  );
  const inventoryQuery = useValidateDraftSalesOrderInventory(
    draftId,
    hasInventoryColumn,
  );
  // Discounts v2: per-line validation status + applied stacks + candidates
  // drive the status column's chip + popover.
  const { results: discountResults } = useValidateDraftDiscountsV2(draftId);
  const { data: stacksData } = useDraftLineStacksV2(draftId);
  const { data: candidatesData } = useDraftDiscountCandidatesV2(draftId);
  const stacksByLine = new Map(
    (stacksData?.stacks ?? []).map((s) => [s.draftLineId, s]),
  );
  const candidatesByLine = new Map(
    (candidatesData?.lines ?? []).map((l) => [l.draftLineId, l.candidates]),
  );

  // Merge the Discounts v2 stack results into each line so custom columns can
  // surface them (e.g. dataPath `discount.effectiveUnitPrice`, format currency).
  const enrichedLineItems: EnrichedLineItem[] = lineItems.map((li) => {
    const stack = stacksByLine.get(li.id);
    const finalUnitPrice =
      stack?.finalUnitPrice != null ? Number(stack.finalUnitPrice) : null;
    return {
      ...li,
      discount: {
        finalUnitPrice,
        effectiveUnitPrice: finalUnitPrice ?? toNum(li.unitPrice),
        totalDiscountAmount:
          stack?.totalDiscountAmount != null
            ? Number(stack.totalDiscountAmount)
            : null,
        pending: stack?.pending ?? false,
      },
    };
  });

  // Display-only filter: totals/validation above still cover every line.
  const filterNeedle = filterText.trim().toLowerCase();
  const visibleLineItems = filterNeedle
    ? enrichedLineItems.filter((li) => lineMatchesFilter(li, filterNeedle))
    : enrichedLineItems;

  function commitPatch(lineItemId: string, patch: LineEditPatch) {
    // A price edit removes the line's applied discount stack server-side (the
    // stack explained the OLD price) — tell the reviewer up front.
    const clearsDiscounts =
      patch.unitPrice !== undefined && stacksByLine.has(lineItemId);
    updateLineItem.mutate(
      { lineItemId, patch },
      {
        onSuccess: () => {
          if (clearsDiscounts) {
            toast.info(
              t(
                'Discounts on this line were removed because the price changed.',
                {
                  context:
                    'toast after editing a unit price on a discounted line',
                },
              ),
            );
          }
        },
        onError: () =>
          toast.error(
            t('Could not save the line item', {
              context: 'sales order line edit error toast',
            }),
          ),
      },
    );
  }

  function renderCell(col: Column, li: EnrichedLineItem): React.ReactNode {
    // A source override (dataPath / compute) wins over the built-in bespoke
    // cell — orgs can re-point catalog columns at any line field.
    if (col.compute) {
      const left = toNum(asNumeric(getByPath(li, col.compute.left)));
      const right = toNum(asNumeric(getByPath(li, col.compute.right)));
      if (left == null || right == null) return '—';
      const result = applyCompute(left, col.compute.op, right);
      return result == null
        ? '—'
        : formatNumber(result, col.format, col.decimals, currency);
    }
    if (col.dataPath) {
      const raw = getByPath(li, col.dataPath);
      const display = formatCustomValue(
        raw,
        col.format,
        currency,
        col.decimals,
      );
      // Manual override: an org-configured editable column on a writable data
      // path renders click-to-edit, committing to the mapped patch field
      // (config alone can't make a derived path editable — the allowlist wins).
      const editSpec = col.editable
        ? EDITABLE_LINE_DATA_PATHS[col.dataPath]
        : undefined;
      const cellEditable =
        editable || (routingEditable && col.dataPath === 'warehouse');
      if (cellEditable && editSpec) {
        const align = columnAlign(col) === 'right' ? 'right' : 'left';
        const textValue = asNumeric(raw);
        const prefix =
          col.format === 'currency' && editSpec.widget === 'number'
            ? getCurrencySymbol(currency)
            : undefined;
        // With the symbol shown as the field prefix, the idle value is the
        // bare amount (else it would read "$ $12.00").
        const numericRaw = toNum(asNumeric(raw));
        const idleDisplay =
          prefix && numericRaw != null
            ? formatPlainAmount(numericRaw)
            : display;
        const edit: InlineEdit =
          editSpec.widget === 'number'
            ? {
                kind: 'number',
                value: toNum(asNumeric(raw)),
                onCommit: (next) =>
                  commitPatch(li.id, buildEditPatch(editSpec, next)),
              }
            : {
                kind: 'text',
                value: textValue == null ? null : String(textValue),
                onCommit: (next) =>
                  commitPatch(li.id, buildEditPatch(editSpec, next)),
              };
        return (
          <InlineEditCell
            edit={edit}
            display={idleDisplay}
            editable
            align={align}
            prefix={prefix}
          />
        );
      }
      return display;
    }
    const qty = toNum(li.quantity);
    const unitPrice = toNum(li.unitPrice);
    switch (col.key) {
      case 'item': {
        // A matched line leads with our internal SKU and keeps the customer's
        // part number alongside in gray; unmatched lines show the customer's
        // identifier alone.
        const showInternalSku =
          !!li.productSku && li.productSku !== li.customerIdentifier;
        const primaryCode = showInternalSku
          ? li.productSku
          : li.customerIdentifier;
        const codeTitle = showInternalSku
          ? `${li.productSku} (${li.customerIdentifier ?? '—'})`
          : (li.customerIdentifier ?? undefined);
        return (
          // Fixed width: long SKUs/descriptions truncate instead of stretching
          // or squashing the other columns.
          <div className="w-56 min-w-0">
            <div className="truncate font-mono text-[12px]" title={codeTitle}>
              {primaryCode ?? '—'}
              {showInternalSku && li.customerIdentifier ? (
                <span className="text-muted-foreground">
                  {' '}
                  ({li.customerIdentifier})
                </span>
              ) : null}
            </div>
            {li.customerDescription ? (
              <div
                className="truncate text-[11px] text-muted-foreground"
                title={li.customerDescription}
              >
                {li.customerDescription}
              </div>
            ) : null}
          </div>
        );
      }
      case 'qty':
        return (
          <InlineEditCell
            edit={{
              kind: 'number',
              value: qty,
              onCommit: (next) => commitPatch(li.id, { quantity: next }),
            }}
            display={qty ?? '—'}
            editable={editable}
            align="right"
          />
        );
      case 'uom':
        return li.unit ?? 'EA';
      case 'price':
        // Price framed as the editable value; the "vs list" delta sits below
        // it, outside the frame, like a configured column's subline.
        return (
          <div className="flex flex-col">
            <InlineEditCell
              edit={{
                kind: 'number',
                value: unitPrice,
                onCommit: (next) => commitPatch(li.id, { unitPrice: next }),
              }}
              editable={editable}
              align="right"
              prefix={getCurrencySymbol(currency)}
              display={
                unitPrice == null ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <span className="tabular-nums">
                    {editable
                      ? formatPlainAmount(unitPrice)
                      : formatCurrency(unitPrice, currency)}
                  </span>
                )
              }
            />
            <span className="text-[11px]">
              <PriceDeltaLine
                unitPrice={li.unitPrice}
                listPrice={li.listPrice}
                currency={currency}
              />
            </span>
          </div>
        );
      case 'total': {
        // Stated line amount first (POs with per-line discounts carry the
        // discounted extension there), else qty × unit price — mirrors the
        // backend's computeLineExtension so every total agrees.
        const statedAmount = li.amount != null ? Number(li.amount) : null;
        const total =
          statedAmount != null && Number.isFinite(statedAmount)
            ? statedAmount
            : calculateTotal(qty, unitPrice);
        return total != null ? formatCurrency(total, currency) : '—';
      }
      case 'inventory':
        if (li.approvalIssue?.excludedFromOrder) {
          return <ExcludedLineChip reason={li.approvalIssue.details} />;
        }
        return (
          <InventoryStatusCell
            result={
              li.selectedProductVariantId
                ? inventoryQuery.data?.results?.[li.selectedProductVariantId]
                : undefined
            }
            fallbackStatus={li.inventoryStatus}
            loading={inventoryQuery.isLoading && hasInventoryColumn}
          />
        );
      case 'warehouse':
        return li.warehouse ?? '—';
      case 'lead': {
        // Fixed lead time stamped by the lead-time extractor at ingest
        // (mirrors v2's read-only Lead Time column).
        const days = li.leadTimeDays;
        if (days == null || days <= 0) return '—';
        return days === 1 ? t('1 day') : `${days} ${t('days')}`;
      }
      case 'discounted_price': {
        const effective = li.discount.effectiveUnitPrice;
        if (effective == null) return '—';
        // Muted while the discount's group is still awaiting approval.
        return (
          <span className={cn(li.discount.pending && 'text-muted-foreground')}>
            {formatCurrency(effective, currency)}
          </span>
        );
      }
      case 'status': {
        // Discounts v2: when the line has validation + a matched variant +
        // customer, the status chip opens the stack popover (candidates,
        // applied math trace, apply/replace/remove).
        const validation = discountResults?.[li.id];
        if (validation && li.selectedProductVariantId && customerId) {
          return (
            <LineDiscountsDialog
              draftId={draftId}
              lineId={li.id}
              validation={validation}
              stack={stacksByLine.get(li.id) ?? null}
              candidates={candidatesByLine.get(li.id) ?? []}
              currency={currency}
              editable={editable}
              customerId={customerId}
              productVariantId={li.selectedProductVariantId}
              sku={li.customerIdentifier ?? `Line ${li.lineNumber + 1}`}
              poUnitPrice={toNum(li.unitPrice)}
              listPrice={toNum(li.listPrice)}
              orderDate={orderDate}
              canCreateDiscount={canCreateDiscount}
              open={
                discountsDialogLineId === undefined
                  ? undefined
                  : discountsDialogLineId === li.id
              }
              onOpenChange={
                onDiscountsDialogLineChange
                  ? (next) => onDiscountsDialogLineChange(next ? li.id : null)
                  : undefined
              }
            />
          );
        }
        // No validation for this line (unmatched product / no customer) —
        // still show the neutral pill so the column reads consistently.
        return <LineStatusChip status={deriveStatus(li) ?? 'none'} />;
      }
      case 'match':
        // Matched/unmatched status icon; opens the product-match popover.
        return (
          <LineMatchPopover
            draftId={draftId}
            lineItem={li}
            customerId={customerId}
            currency={currency}
            editable={editable}
          />
        );
      // Custom sources are handled above; an unknown key with no source
      // renders nothing.
      default:
        return null;
    }
  }

  /** The configured sub-line under a cell's main value (prefix + value). */
  function renderSubline(col: Column, li: EnrichedLineItem): React.ReactNode {
    const sub = col.subline;
    if (!sub) return null;
    let value: React.ReactNode = null;
    if (sub.compute) {
      const left = toNum(asNumeric(getByPath(li, sub.compute.left)));
      const right = toNum(asNumeric(getByPath(li, sub.compute.right)));
      if (left == null || right == null) return null;
      const result = applyCompute(left, sub.compute.op, right);
      if (result == null) return null;
      value = formatNumber(result, sub.format, sub.decimals, currency);
    } else if (sub.dataPath) {
      const raw = getByPath(li, sub.dataPath);
      if (raw == null || raw === '') return null;
      value = formatCustomValue(raw, sub.format, currency, sub.decimals);
    } else {
      return null;
    }
    return (
      <div className="whitespace-nowrap text-[11px] text-muted-foreground">
        {sub.prefix ? (
          <span className="font-medium">{sub.prefix}: </span>
        ) : null}
        <span className="tabular-nums">{value}</span>
      </div>
    );
  }

  if (lineItems.length === 0) {
    return (
      <p className="px-1 py-6 text-sm text-muted-foreground">
        <T>No line items.</T>
      </p>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8 text-center">#</TableHead>
            {columns.map((col) => (
              <TableHead
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={cn(
                  columnAlign(col) === 'right' && 'text-right',
                  columnAlign(col) === 'center' && 'text-center',
                )}
              >
                <span
                  className={cn(
                    'inline-flex items-center gap-1 align-middle',
                    columnAlign(col) === 'right' && 'justify-end',
                  )}
                >
                  {col.label}
                  {col.tooltip ? (
                    <TooltipInfoIcon
                      description={col.tooltip}
                      ariaLabel={col.label}
                    />
                  ) : null}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleLineItems.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + 1}
                className="py-6 text-center text-sm text-muted-foreground"
              >
                <T context="sales order line table: no rows match the search filter">
                  No lines match
                </T>
              </TableCell>
            </TableRow>
          ) : null}
          {visibleLineItems.map((li) => (
            <TableRow
              key={li.id}
              data-line-id={li.id}
              onMouseEnter={() => setHoveredLineId(li.id)}
              onMouseLeave={() => setHoveredLineId(null)}
              className={cn(
                // `data-nami-focus` is set for a moment by the AI assistant's
                // focus_order_line tool after it scrolls the row into view.
                'transition-colors data-[nami-focus=true]:bg-primary/10',
                hoveredLineId === li.id && 'bg-blue-500/10',
              )}
            >
              {/* Fixed-size cell: the number and the hover trashcan share one
                  constant-size box so swapping them never resizes the column. */}
              <TableCell className="w-8 px-1 text-muted-foreground">
                <div className="flex h-6 w-full items-center justify-center">
                  {editable && hoveredLineId === li.id ? (
                    <button
                      type="button"
                      className="flex size-6 items-center justify-center rounded-sm text-destructive hover:bg-destructive/10 disabled:opacity-50"
                      title={t('Remove line item')}
                      disabled={deleteLineItem.isPending}
                      onClick={() =>
                        deleteLineItem.mutate(li.id, {
                          onError: () =>
                            toast.error('Could not remove the line item'),
                        })
                      }
                    >
                      <TrashIcon className="size-4" />
                    </button>
                  ) : (
                    // Same fixed footprint as the trash button so the column
                    // width never shifts on hover.
                    <span className="flex size-6 items-center justify-center tabular-nums">
                      {li.lineNumber + 1}
                    </span>
                  )}
                </div>
              </TableCell>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={cn(
                    columnAlign(col) === 'right' && 'text-right tabular-nums',
                    columnAlign(col) === 'center' && 'text-center',
                  )}
                >
                  {renderCell(col, li)}
                  {renderSubline(col, li)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}
